/**
 * panelResize — pure pixel math for the drag-to-resize handles.
 *
 * A handle IS the right edge of the panel before it, and dragging it resizes
 * that one panel: right to grow, left to shrink. Whatever it gains or gives up
 * comes from the slots to its right, elastic ones first — a `fill` panel, and
 * the free space the caller appends as the row's last slot — then the
 * remaining panels outward, each taking part until it hits a bound.
 *
 * Two things follow, and both are the point. A drag with room to its right
 * spends that room, so a neighbour that was set to a width keeps it and simply
 * slides along; and a panel can always be narrowed, because the width it
 * releases has the free space to fall into even when every neighbour is
 * already at its maximum. Reaching past the elastic to shrink or grow a
 * neighbour is the fallback, not the rule.
 *
 * Elastic slots never trade with each other: a fill panel is the row's
 * remainder, and width it handed to the free space would come straight back
 * on release. Dragging a fill panel's edge therefore trades with the set
 * panels beyond it only.
 *
 * The total across the row is conserved, free slot included — that is what
 * lets a panel grow into trailing space and hand it back. allocate() only ever
 * feeds `fill` panels, so space the row does not claim stays trailing rather
 * than being handed out again (see allocate.ts).
 */

import type { UserWidths } from '@/types/panels';

const EPS = 1e-6;

/** A slot the drag can trade width with — a panel, or the trailing free space. */
interface ResizableSlot {
    id?: string | null;
    widthPx: number;
    min: number;
    max: number;
    fill?: boolean;
}

/**
 * @param panels ordered left to right, the trailing free slot included.
 * @param boundaryIndex index of the first slot right of the handle; the panel
 *   the drag resizes is the one before it.
 * @param deltaPx signed pointer delta, positive when the handle moved right.
 * @returns the new pixel width per panel, aligned to `panels`.
 */
export function applyResize(
    panels: ResizableSlot[], boundaryIndex: number, deltaPx: number, eps = EPS,
): number[] {
    const widths = panels.map((panel) => panel.widthPx);
    const resizedIndex = boundaryIndex - 1;
    const resizedSlot = panels[resizedIndex];
    const resizedWidth = widths[resizedIndex];
    if (resizedSlot === undefined || resizedWidth === undefined) {
        return widths;
    }

    // Everything right of the handle takes part. Stable sort, so the elastic
    // slots jump the queue and the rest keep their nearest-first order behind.
    const others: { index: number; slot: ResizableSlot }[] = [];
    for (const [index, slot] of panels.entries()) {
        if (index >= boundaryIndex && !(resizedSlot.fill && slot.fill)) {
            others.push({ index, slot });
        }
    }
    others.sort((a, b) => (b.slot.fill ? 1 : 0) - (a.slot.fill ? 1 : 0));

    const isGrowing = deltaPx >= 0;
    const targetMovePx = isGrowing
        ? Math.min(deltaPx, resizedSlot.max - resizedWidth)
        : Math.min(-deltaPx, resizedWidth - resizedSlot.min);

    let movedPx = 0;
    for (const { index, slot } of others) {
        if (targetMovePx - movedPx <= eps) {
            break;
        }
        const width = widths[index];
        if (width === undefined) { continue; }
        const room = isGrowing ? width - slot.min : slot.max - width;
        const step = Math.min(room, targetMovePx - movedPx);
        if (step > eps) {
            widths[index] = width + (isGrowing ? -step : step);
            movedPx += step;
        }
    }
    widths[resizedIndex] = resizedWidth + (isGrowing ? movedPx : -movedPx);
    return widths;
}

/**
 * The widths to persist once a drag is released: every panel the drag left at
 * a different width than it started, keyed by id. Elastic slots are skipped —
 * the free space is not a panel, and a fill panel is only ever the row's
 * remainder — so neither has a width to remember.
 */
export function dragUpdates(
    snapshot: (ResizableSlot & { id: string | null })[], widths: number[], eps = EPS,
): Record<string, number> {
    const updates: Record<string, number> = {};
    snapshot.forEach((panel, index) => {
        const width = widths[index];
        if (width === undefined) { return; }
        if (!panel.fill && panel.id !== null && Math.abs(width - panel.widthPx) > eps) {
            updates[panel.id] = width;
        }
    });
    return updates;
}

/** Merge persisted widths, handing back the same object when nothing moved. */
export function mergePanelWidths(
    currentUserWidths: UserWidths = {}, updates: UserWidths = {},
): UserWidths {
    let nextUserWidths = currentUserWidths;
    for (const [id, value] of Object.entries(updates)) {
        if (nextUserWidths[id] === value) {
            continue;
        }
        if (nextUserWidths === currentUserWidths) {
            nextUserWidths = { ...currentUserWidths };
        }
        nextUserWidths[id] = value;
    }
    return nextUserWidths;
}
